/**
 * SkeletonRow — Animated shimmer placeholder row for table loading state.
 *
 * Renders column-width-aware shimmer bars that match the table's layout,
 * providing a visual loading indicator while data is being fetched.
 */

import { cx } from '@/utils/cx';

import { styles } from '../styles';
import type { DataTableColumnConfig } from '../types';

// ---------------------------------------------------------------------------
// Helpers (duplicated from data-table to avoid circular imports)
// ---------------------------------------------------------------------------

function getColWidthFromConfig(
  col: DataTableColumnConfig,
  defaultColumnWidth: number
): number {
  if (typeof col.width === 'number') {
    return col.width;
  }
  if (typeof col.width === 'string') {
    const parsed = parseFloat(col.width);
    if (
      !isNaN(parsed) &&
      !col.width.includes('%') &&
      !col.width.includes('fr')
    ) {
      return parsed;
    }
  }
  return defaultColumnWidth;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SkeletonRowProps {
  columns: DataTableColumnConfig[];
  size: keyof typeof styles.sizes;
  defaultColumnWidth: number;
  includeCheckbox?: boolean;
  checkboxWidth?: number;
  index: number;
  /**
   * Optional function to get the actual column width (from resize hook).
   * When provided, overrides the column config width.
   */
  getColumnWidth?: (colId: string, defaultWidth: number) => number;
}

// ---------------------------------------------------------------------------
// SkeletonCell
// ---------------------------------------------------------------------------

const SkeletonCell = ({
  width,
  size,
  barWidthPercent,
}: {
  width: number;
  size: keyof typeof styles.sizes;
  barWidthPercent: number;
}) => (
  <div
    role='gridcell'
    aria-hidden='true'
    style={{ width, minWidth: width }}
    className={cx(styles.common.cell, styles.sizes[size].cell)}
  >
    <div
      className='h-3 rounded animate-pulse bg-[var(--color-bg-tertiary)]'
      style={{ width: `${barWidthPercent}%` }}
    />
  </div>
);

// ---------------------------------------------------------------------------
// SkeletonRow
// ---------------------------------------------------------------------------

/**
 * The inner cells of a skeleton row — the optional checkbox cell plus one
 * shimmer `SkeletonCell` per column — WITHOUT any row wrapper.
 *
 * Rendered directly inside a caller-owned `.row` div (see the virtualizer's
 * placeholder branch in data-table.tsx) so a skeleton row is a SINGLE `.row`,
 * exactly like a real data row. Wrapping this in its own `.row` (as `SkeletonRow`
 * does for the standalone initial-load path) inside another `.row` is what
 * produced the doubled `border-b` and nested `min-height` the loading state
 * used to show.
 */
export const SkeletonRowCells = ({
  columns,
  size,
  defaultColumnWidth,
  includeCheckbox = false,
  checkboxWidth = 48,
  index,
  getColumnWidth,
}: SkeletonRowProps) => {
  // Vary bar widths across columns and rows for a natural look
  const getBarWidthPercent = (colIndex: number): number => {
    const patterns = [60, 80, 45, 70, 55, 75, 50, 65];
    return patterns[(colIndex + index) % patterns.length];
  };

  return (
    <>
      {includeCheckbox && (
        <div
          role='gridcell'
          aria-hidden='true'
          style={{ width: checkboxWidth, minWidth: checkboxWidth }}
          // `border-r-0` matches the real table's `renderCheckboxCell`
          // (data-table.tsx:1169) which deliberately drops the cell's
          // right border so the checkbox column blends with its pinned
          // neighbor. Without this override the skeleton inherits the
          // default `border-r` from `styles.common.cell`, producing a
          // vertical divider during loading that disappears the moment
          // real data renders — a visual hiccup the user flagged.
          className={cx(
            styles.common.cell,
            styles.sizes[size].cell,
            'flex items-center justify-center border-r-0'
          )}
        >
          <div className='h-4 w-4 rounded animate-pulse bg-[var(--color-bg-tertiary)]' />
        </div>
      )}
      {columns.map((col, colIndex) => {
        const colId = String(col.name ?? col.kind ?? colIndex);
        // Use resize hook's width if available, otherwise fall back to config width
        const configWidth = getColWidthFromConfig(col, defaultColumnWidth);
        const colWidth = getColumnWidth
          ? getColumnWidth(colId, configWidth)
          : configWidth;
        return (
          <SkeletonCell
            key={colId}
            width={colWidth}
            size={size}
            barWidthPercent={getBarWidthPercent(colIndex)}
          />
        );
      })}
    </>
  );
};

/**
 * A single shimmer row placeholder matching the table column layout — a `.row`
 * wrapper around `SkeletonRowCells`. Use this for the standalone loading state
 * (`SkeletonRows`), where the skeleton owns its row element. Inside the
 * virtualizer, where the placeholder already provides the positioned `.row`
 * div, render `SkeletonRowCells` directly instead so the row isn't doubled.
 */
export const SkeletonRow = (props: SkeletonRowProps) => (
  <div
    role='row'
    aria-hidden='true'
    className={cx(
      styles.common.row,
      styles.sizes[props.size].row,
      'pointer-events-none'
    )}
  >
    <SkeletonRowCells {...props} />
  </div>
);

// ---------------------------------------------------------------------------
// SkeletonRows — renders N skeleton rows for loading state
// ---------------------------------------------------------------------------

interface SkeletonRowsProps {
  count?: number;
  columns: DataTableColumnConfig[];
  size: keyof typeof styles.sizes;
  defaultColumnWidth: number;
  includeCheckbox?: boolean;
  checkboxWidth?: number;
  /**
   * Optional function to get the actual column width (from resize hook).
   * When provided, overrides the column config width.
   */
  getColumnWidth?: (colId: string, defaultWidth: number) => number;
}

export const SkeletonRows = ({
  count = 10,
  columns,
  size,
  defaultColumnWidth,
  includeCheckbox,
  checkboxWidth,
  getColumnWidth,
}: SkeletonRowsProps) =>
  Array.from({ length: count }, (_, i) => (
    <SkeletonRow
      key={`skeleton-${i}`}
      index={i}
      columns={columns}
      size={size}
      defaultColumnWidth={defaultColumnWidth}
      includeCheckbox={includeCheckbox}
      checkboxWidth={checkboxWidth}
      getColumnWidth={getColumnWidth}
    />
  ));
